import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';

import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { CalendarTimeFormat } from '../calendar.models';
import { CalendarEventLayout } from '../layout/calendar-event-layout.model';

const DEFAULT_FONT_SIZE = 13;

@Component({
  selector: 'blaze-calendar-event',
  templateUrl: './calendar-event.component.html',
  styleUrls: ['./calendar-event.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarEventComponent implements OnChanges {
  details = output();

  @Input() calendarEventLayout!: CalendarEventLayout;
  @Input() fontSize: ValueWithUnitOfMeasure = `${DEFAULT_FONT_SIZE}px`;
  @Input() height = 0;
  @Input() timeFormat: CalendarTimeFormat = CalendarTimeFormat['12H'];
  @Input() timeScale = 1;

  titleLines = 2;
  displaySecondLine = true;
  shrink = false;
  time = '';
  effectiveFontSize: ValueWithUnitOfMeasure = `${DEFAULT_FONT_SIZE}px`;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.calendarEventLayout) {
      this.setEventTime();
    }
    if (changes.fontSize || changes.timeScale || changes.calendarEventLayout) {
      this.updateFontSize();
    }
  }

  onEventClick(): void {
    this.details.emit();
  }

  private setEventTime(): void {
    const startDate =
      this.calendarEventLayout.event.originalStartDate ?? this.calendarEventLayout.event.startDate;

    const endDate =
      this.calendarEventLayout.event.originalEndDate ?? this.calendarEventLayout.event.endDate;

    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: this.timeFormat === CalendarTimeFormat['12H'],
      minute: '2-digit',
    });

    let time = formatter.format(startDate);

    if (endDate) {
      time += ` - ${formatter.format(endDate)}`;
    }

    this.time = time;
  }

  private updateFontSize(): void {
    const fontSizePxStr = /^(\d+)px$/.exec(this.fontSize)?.[1];
    if (!fontSizePxStr) {
      console.error(`unsupported font size unit: ${this.fontSize}`);
    }
    const fontSizePx = fontSizePxStr ? parseFloat(fontSizePxStr) : DEFAULT_FONT_SIZE;

    const normalLineHeightScale = 1.2;
    const verticalPadding = 16;
    const lineHeight = fontSizePx * normalLineHeightScale;
    const lineCount = Math.floor((this.height - verticalPadding) / lineHeight);
    this.shrink = lineCount <= 0;
    this.titleLines = lineCount >= 3 ? 2 : 1;
    this.displaySecondLine = lineCount >= 2;
    this.effectiveFontSize = this.shrink
      ? `${Math.min(fontSizePx, Math.floor(this.height / normalLineHeightScale))}px`
      : this.fontSize;
  }
}
